import { Injectable, Injector, NgZone } from '@angular/core';
import { AppComponentBase } from '@shared/common/app-component-base';
import { HubConnection } from '@microsoft/signalr';

@Injectable()
export class ChatSignalrService extends AppComponentBase {

    constructor(
        injector: Injector,
        public _zone: NgZone
    ) {
        super(injector);
    }

    chatHub: HubConnection;
    isChatConnected = false;
    private chatReconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private chatReconnectDelayMs = 2000;
    private readonly chatReconnectDelayMaxMs = 60000;
    private chatReconnectAttempts = 0;
    private readonly chatReconnectAttemptsBeforeLongBackoff = 10;

   configureConnection(connection): void {
        // Set the common hub
        this.chatHub = connection;

        const scheduleReconnect = () => {
            if (this.chatReconnectTimer) {
                clearTimeout(this.chatReconnectTimer);
            }
            if (this.chatReconnectAttempts >= this.chatReconnectAttemptsBeforeLongBackoff) {
                abp.log.warn(
                    'Chat SignalR: many failed reconnects; backing off. Fix the signalr-chat hub on the server (see API logs).'
                );
                this.chatReconnectDelayMs = this.chatReconnectDelayMaxMs;
                this.chatReconnectAttempts = 0;
            }
            this.chatReconnectTimer = setTimeout(() => {
                this.chatReconnectTimer = null;
                connection
                    .start()
                    .then(() => {
                        this.isChatConnected = true;
                        this.chatReconnectDelayMs = 2000;
                        this.chatReconnectAttempts = 0;
                        abp.event.trigger('app.chat.connected');
                    })
                    .catch((err) => {
                        abp.log.debug('Chat SignalR reconnect failed: ' + (err && err.message ? err.message : err));
                        this.chatReconnectAttempts++;
                        this.chatReconnectDelayMs = Math.min(
                            this.chatReconnectDelayMaxMs,
                            Math.max(2000, this.chatReconnectDelayMs * 2)
                        );
                        scheduleReconnect();
                    });
            }, this.chatReconnectDelayMs);
        };

        // Reconnect if hub disconnects (always delay — immediate start() caused connect/close tight loops)
        connection.onclose(e => {
            this.isChatConnected = false;

            if (e) {
                abp.log.debug('Chat connection closed with error: ' + e);
            } else {
                abp.log.debug('Chat disconnected');
            }

            this.chatReconnectAttempts++;
            this.chatReconnectDelayMs = Math.min(
                this.chatReconnectDelayMaxMs,
                Math.max(2000, this.chatReconnectDelayMs * 2)
            );
            scheduleReconnect();
        });

        // Register to get notifications
        this.registerChatEvents(connection);
    }

    registerChatEvents(connection): void {
        connection.on('getChatMessage', message => {
            abp.event.trigger('app.chat.messageReceived', message);
        });

        connection.on('getAllFriends', friends => {
            abp.event.trigger('abp.chat.friendListChanged', friends);
        });

        connection.on('getFriendshipRequest', (friendData, isOwnRequest) => {
            abp.event.trigger('app.chat.friendshipRequestReceived', friendData, isOwnRequest);
        });

        connection.on('getUserConnectNotification', (friend, isConnected) => {
            abp.event.trigger('app.chat.userConnectionStateChanged',
                {
                    friend: friend,
                    isConnected: isConnected
                });
        });

        connection.on('getUserStateChange', (friend, state) => {
            abp.event.trigger('app.chat.userStateChanged',
                {
                    friend: friend,
                    state: state
                });
        });

        connection.on('getallUnreadMessagesOfUserRead', friend => {
            abp.event.trigger('app.chat.allUnreadMessagesOfUserRead',
                {
                    friend: friend
                });
        });

        connection.on('getReadStateChange', friend => {
            abp.event.trigger('app.chat.readStateChange',
                {
                    friend: friend
                });
        });
    }

    sendMessage(messageData, callback): void {
        if (!this.isChatConnected) {
            if (callback) {
                callback();
            }

            abp.notify.warn(this.l('ChatIsNotConnectedWarning'));
            return;
        }

        this.chatHub.invoke('sendMessage', messageData).then(result => {
            if (result) {
                abp.notify.warn(result);
            }

            if (callback) {
                callback();
            }
        }).catch(error => {
            abp.log.error(error);

            if (callback) {
                callback();
            }
        });
    }

    init(): void {
        this._zone.runOutsideAngular(() => {
            abp.signalr.connect();
            abp.signalr.startConnection(abp.appPath + 'signalr-chat', connection => {
                this.configureConnection(connection);
            }).then(() => {
                abp.event.trigger('app.chat.connected');
                this.isChatConnected = true;
            });
        });
    }
}
